import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaShopify } from 'react-icons/fa';
import ShopifyProductPicker from './components/Shopify/ShopifyProductPicker';
import PlatformSelector from './components/Shopify/PlatformSelector';
import InlineCreativeStudio from './components/Shopify/InlineCreativeStudio';
import CreateAd from './CreateAd';
import AiChatBot from '../AiChatBot/AiChatBot';
import { CreateAdProvider, useCreateAdContext } from '../../context/CreateAdContext';
import { getRequest, postRequest } from '../../services/api/apiService';

const STEPS = ['Produit', 'Plateforme', 'Publier'];

// Dispatche le pré-remplissage IA (DeepSeek, généré côté serveur à la création
// du brouillon) dans l'accordéon CreateAd via le protocole d'actions d'Alex —
// chaque champ est appliqué et surligné exactement comme quand Alex remplit
// le formulaire depuis le chat.
const PrefillDispatcher = ({ form }) => {
  const { dispatchActions } = useCreateAdContext();
  const doneRef = useRef(false);
  useEffect(() => {
    if (doneRef.current || !form) return;
    doneRef.current = true;
    const actions = Object.entries(form)
      .filter(([f, v]) => v !== null && v !== undefined && !String(f).startsWith('_'))
      .map(([field, value]) => ({ field, value }));
    // Petit délai : laisse CreateAd monter ses sections avant la vague de champs.
    if (actions.length) setTimeout(() => dispatchActions(actions), 400);
  }, [form, dispatchActions]);
  return null;
};

const CreateAdFromShopify = ({ isPremium }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'disconnected' | 'connected'
  const [connectedShop, setConnectedShop] = useState('');
  const [shopInput, setShopInput] = useState('');
  const [connecting, setConnecting] = useState(false);
  // Connexion alternative par token Admin API (custom app « Develop apps »)
  // — utile quand l'app OAuth Partner n'est pas disponible.
  const [manualToken, setManualToken] = useState('');
  const [manualConnecting, setManualConnecting] = useState(false);
  const [manualMsg, setManualMsg] = useState(null); // { ok, text }

  const handleManualConnect = async () => {
    const shop = shopInput.trim();
    if (!shop || !manualToken.trim()) return;
    setManualConnecting(true);
    setManualMsg(null);
    try {
      const res = await postRequest('/api/ads-auth/shopify-oauth-ads/manual-token', {
        shop,
        accessToken: manualToken.trim(),
      });
      const pixel = res.pixelInstall;
      setManualMsg({
        ok: true,
        text: `Boutique « ${res.shopName || shop} » connectée (${(res.scopes || []).length} scopes). ` +
          (pixel?.installed
            ? 'Pixel de tracking installé automatiquement ✅'
            : `Pixel non installé : ${pixel?.reason || 'ajoutez le scope write_script_tags à votre app personnalisée.'}`),
      });
      setManualToken('');
      checkStatus();
    } catch (err) {
      setManualMsg({ ok: false, text: err?.response?.data?.message || 'Connexion par token échouée.' });
    } finally {
      setManualConnecting(false);
    }
  };
  const [step, setStep] = useState(1); // 1=products, 2=platform, 3=publish
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [isAIMode, setIsAIMode] = useState(false);
  // Brouillon persisté (table shopify_ad_drafts) : créé au passage à l'étape 3
  // avec le pré-remplissage IA — survit au détour Cinema Studio (?draft=ID).
  const [draft, setDraft] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  // 'ask' = question « besoin d'une créa IA ? » | 'studio' = studio inline
  // ouvert (création SANS quitter l'écran) | 'done' = tranché.
  const [creativeChoice, setCreativeChoice] = useState('ask');

  useEffect(() => {
    checkStatus();
  }, []);

  // Retour du Cinema Studio (ou reprise d'un brouillon) : ?draft=ID restaure
  // produit + plateforme + formulaire IA + créa attachée, direct à l'étape 3.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('draft');
    if (!id) return;
    setDraftLoading(true);
    getRequest(`/api/shopify-ad-drafts/${id}`)
      .then((r) => {
        if (!r.draft) return;
        setDraft(r.draft);
        setSelectedProduct(r.draft.product);
        setSelectedPlatform(r.draft.platform);
        setCreativeChoice(r.draft.creative ? 'done' : 'ask');
        setStep(3);
      })
      .catch(() => {})
      .finally(() => setDraftLoading(false));
  }, []);

  // Étape 2 → 3 : crée le brouillon en BD et génère le pré-remplissage IA.
  const goToPublish = async () => {
    setStep(3);
    setDraftLoading(true);
    try {
      const res = await postRequest('/api/shopify-ad-drafts', {
        product: selectedProduct,
        platform: selectedPlatform,
      });
      setDraft(res.draft);
      setCreativeChoice('ask');
    } catch {
      setDraft(null); // le tunnel reste utilisable sans brouillon (pré-remplissage produit seul)
    } finally {
      setDraftLoading(false);
    }
  };

  const checkStatus = async () => {
    try {
      const res = await getRequest('/api/ads/shopify-oauth/status');
      if (res.connected) {
        setConnectedShop(res.shop || '');
        setStatus('connected');
      } else {
        setStatus('disconnected');
      }
    } catch {
      setStatus('disconnected');
    }
  };

  const handleConnect = () => {
    const shop = shopInput.trim().replace(/\.myshopify\.com$/i, '');
    if (!shop) return;
    setConnecting(true);
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    window.location.href = `${apiBase}/api/ads-auth/shopify-oauth-ads/?shop=${encodeURIComponent(shop)}`;
  };

  const handleDisconnect = async () => {
    try {
      await postRequest('/api/connected-platforms/disconnect', { platform: 'shopify-oauth' });
    } catch {
      // ignore — still reset UI
    }
    setSelectedProduct(null);
    setSelectedPlatform(null);
    setStep(1);
    setShopInput('');
    setConnectedShop('');
    setStatus('disconnected');
  };

  // ── Loading ──────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <div className="w-8 h-8 border-4 border-[#221C51]/20 border-t-[#221C51] rounded-full animate-spin" />
        <p className="text-sm text-gray-400 mt-4">Vérification de la connexion Shopify…</p>
      </div>
    );
  }

  // ── Not connected ─────────────────────────────────────────
  if (status === 'disconnected') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] px-4">
        <div className="w-full max-w-md text-center">
          <div className="flex items-center justify-center w-20 h-20 rounded-2xl mx-auto mb-6 shadow-lg" style={{ backgroundColor: '#96bf48' }}>
            <FaShopify className="text-4xl text-white" />
          </div>
          <h2 className="text-2xl font-bold text-[#221C51] mb-2">Connectez votre boutique Shopify</h2>
          <p className="text-sm text-gray-500 mb-8 leading-relaxed">
            Reliez votre boutique pour importer vos produits et créer des publicités en quelques clics sur Meta, TikTok, Google et plus.
          </p>

          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 text-left shadow-sm">
            <label className="block text-sm font-semibold text-[#221C51] mb-1">
              Nom de votre boutique <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center border border-gray-300 rounded-xl overflow-hidden bg-white focus-within:border-[#221C51] transition">
              <input
                type="text"
                value={shopInput}
                onChange={(e) => setShopInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
                placeholder="ma-boutique"
                className="flex-1 px-4 py-3 text-sm focus:outline-none bg-transparent"
              />
              <span className="px-3 py-3 text-xs text-gray-400 bg-gray-50 border-l border-gray-200 whitespace-nowrap select-none">
                .myshopify.com
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Entrez uniquement le nom, sans ".myshopify.com"
            </p>
            <button
              onClick={handleConnect}
              disabled={!shopInput.trim() || connecting}
              className="mt-5 w-full py-3 text-white font-semibold rounded-xl transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ backgroundColor: '#96bf48' }}
            >
              {connecting ? (
                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <FaShopify className="text-lg" />
              )}
              {connecting ? 'Redirection…' : 'Connecter ma boutique →'}
            </button>

            {/* Alternative : token Admin API d'une app personnalisée (Develop apps) */}
            <details className="mt-4 text-left">
              <summary className="text-xs text-gray-400 hover:text-[#221C51] cursor-pointer select-none">
                🔑 Ou connecter via une app personnalisée (token Admin API)
              </summary>
              <div className="mt-3 space-y-2">
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  Admin Shopify → Paramètres → Applications → <b>Développer des applications</b> → votre app →
                  cochez les scopes (dont <code className="bg-gray-100 px-1 rounded">write_script_tags</code>) →
                  installez → copiez le token <code className="bg-gray-100 px-1 rounded">shpat_…</code> ci-dessous.
                </p>
                <input
                  type="password"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value)}
                  placeholder="shpat_xxxxxxxxxxxxxxxx"
                  autoComplete="off"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51] bg-white"
                />
                <button
                  onClick={handleManualConnect}
                  disabled={!shopInput.trim() || !manualToken.trim() || manualConnecting}
                  className="w-full py-2.5 text-sm font-semibold text-[#221C51] border border-[#221C51]/40 rounded-xl hover:bg-[#221C51]/5 transition disabled:opacity-40"
                >
                  {manualConnecting ? 'Validation du token…' : 'Connecter avec ce token'}
                </button>
                {manualMsg && (
                  <p className={`text-xs leading-relaxed ${manualMsg.ok ? 'text-emerald-700' : 'text-red-600'}`}>
                    {manualMsg.text}
                  </p>
                )}
              </div>
            </details>
          </div>

          <div className="mt-8 flex items-start gap-3 text-left">
            {['Importez vos produits', 'Choisissez votre plateforme', 'Publiez en un clic'].map((txt, i) => (
              <div key={i} className="flex-1 flex flex-col items-center text-center">
                <div className="w-8 h-8 rounded-full bg-[#221C51] text-white text-xs font-bold flex items-center justify-center mb-2 flex-shrink-0">
                  {i + 1}
                </div>
                <p className="text-xs text-gray-500">{txt}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Connected — 3-step wizard ─────────────────────────────
  const canNext = step === 1 ? !!selectedProduct : !!selectedPlatform;

  return (
    <div className="p-4 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FaShopify className="text-3xl flex-shrink-0" style={{ color: '#96bf48' }} />
          <div>
            <h2 className="text-2xl font-semibold text-[#221C51]">Créer une pub depuis Shopify</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Connecté à&nbsp;
              <span className="font-semibold text-[#221C51]">
                {connectedShop ? connectedShop : 'votre boutique'}
              </span>
            </p>
          </div>
        </div>
        <button
          onClick={handleDisconnect}
          className="text-xs text-gray-400 hover:text-red-500 transition underline"
        >
          Changer de boutique
        </button>
      </div>
      <hr className="border-t border-[#221C51] mb-6" />

      {/* Step indicator — hidden on step 3 since CreateAd has its own header.
          Les boutons Retour/Continuer sont ICI, en haut à droite : avec 20+
          produits, l'ancien emplacement (sous la grille) obligeait à scroller
          toute la liste pour avancer. */}
      {step < 3 && (
        <div className="flex items-center justify-end gap-2 mb-3">
          <button
            type="button"
            onClick={() => setStep((s) => s - 1)}
            disabled={step === 1}
            className="px-5 py-2 text-sm text-[#221C51] border border-[#221C51]/30 rounded-lg disabled:opacity-30 hover:bg-[#221C51]/5 transition"
          >
            ← Retour
          </button>
          <button
            type="button"
            onClick={() => (step === 2 ? goToPublish() : setStep((s) => s + 1))}
            disabled={!canNext}
            className="px-6 py-2 text-sm text-white bg-[#221C51] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#2e265e] transition"
          >
            Continuer →
          </button>
        </div>
      )}
      {step < 3 && (
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((label, i) => {
            const idx = i + 1;
            return (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-2 ${idx <= step ? 'text-[#221C51]' : 'text-gray-300'}`}>
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      idx < step
                        ? 'bg-[#221C51] text-white'
                        : idx === step
                        ? 'bg-[#221C51] text-white'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {idx < step ? '✓' : idx}
                  </div>
                  <span className="text-sm font-semibold whitespace-nowrap">{label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 ${idx < step ? 'bg-[#221C51]' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Step 1 : Product picker */}
      {step === 1 && (
        <ShopifyProductPicker selected={selectedProduct} onSelect={setSelectedProduct} />
      )}

      {/* Step 2 : Platform selector */}
      {step === 2 && (
        <div>
          <div className="flex items-center gap-4 mb-6 p-4 bg-[#221C51]/5 rounded-xl border border-[#221C51]/10">
            {selectedProduct.image_url && (
              <img
                src={selectedProduct.image_url}
                alt={selectedProduct.title}
                className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#221C51] text-sm truncate">{selectedProduct.title}</p>
              {selectedProduct.price && (
                <p className="text-xs text-gray-500 mt-0.5">{selectedProduct.price}</p>
              )}
            </div>
            <button
              onClick={() => setStep(1)}
              className="text-xs text-[#221C51] underline flex-shrink-0"
            >
              Changer
            </button>
          </div>
          <h3 className="text-base font-semibold text-[#221C51] mb-4">
            Sur quelle plateforme diffuser ?
          </h3>
          <PlatformSelector selected={selectedPlatform} onSelect={setSelectedPlatform} />
        </div>
      )}

      {/* Step 3 : CreateAd pre-filled — AI mode handled here to preserve product data */}
      {step === 3 && (
        <>
          {/* Génération IA du paramétrage (DeepSeek) en cours */}
          {draftLoading && (
            <div className="flex items-center gap-3 p-4 mb-4 bg-indigo-50 border border-indigo-200 rounded-2xl">
              <div className="w-5 h-5 border-2 border-indigo-300 border-t-indigo-700 rounded-full animate-spin flex-shrink-0" />
              <p className="text-sm text-indigo-800">
                🤖 L'IA génère le paramétrage de votre campagne (nom, textes, ciblage, budget)…
              </p>
            </div>
          )}

          {/* Question créa : studio IA intégré, SANS quitter l'écran */}
          {!draftLoading && draft && !draft.creative && creativeChoice === 'ask' && (
            <div className="p-4 mb-4 bg-fuchsia-50 border border-fuchsia-200 rounded-2xl">
              <p className="text-sm font-bold text-fuchsia-900 mb-1">
                🎬 Besoin d'une vidéo ou d'une image générée par IA pour cette pub ?
              </p>
              <p className="text-xs text-fuchsia-700 mb-3">
                UGC, cinématographique, photo produit… le modèle le plus adapté (Seedance, Kling, Veo, Imagen)
                est recommandé automatiquement — et tout se passe ici, sur cet écran.
              </p>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setCreativeChoice('studio')}
                  className="px-4 py-2 text-xs font-semibold text-white bg-fuchsia-600 rounded-xl hover:bg-fuchsia-700 transition"
                >
                  ✨ Oui, créer ma créa ici
                </button>
                <button
                  onClick={() => setCreativeChoice('done')}
                  className="px-4 py-2 text-xs font-semibold text-fuchsia-800 border border-fuchsia-300 rounded-xl hover:bg-fuchsia-100 transition"
                >
                  Non merci, j'ai déjà ma créa
                </button>
                <button
                  onClick={() => navigate(`/dashboard/cinema-studio?draft=${draft.id}`)}
                  className="px-4 py-2 text-xs text-fuchsia-700 underline hover:text-fuchsia-900 transition"
                  title="Tous les outils : avatars, voix off, try-on, motion control…"
                >
                  Ouvrir le Cinema Studio complet →
                </button>
              </div>
            </div>
          )}

          {/* Studio créa inline — génération, recréation, édition Veo, attache.
              Reste ouvrable même avec une créa déjà attachée (Régénérer). */}
          {!draftLoading && draft && creativeChoice === 'studio' && (
            <InlineCreativeStudio
              product={selectedProduct}
              draftId={draft.id}
              onAttached={(updated) => { setDraft(updated); setCreativeChoice('done'); }}
            />
          )}

          {/* Créa attachée depuis le Cinema Studio */}
          {draft?.creative?.url && (
            <div className="p-4 mb-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-3 flex-wrap">
              {draft.creative.type === 'video' ? (
                <video src={draft.creative.url} className="w-20 h-20 rounded-lg object-cover border border-emerald-200" muted playsInline />
              ) : (
                <img src={draft.creative.url} alt="Créa" className="w-20 h-20 rounded-lg object-cover border border-emerald-200" />
              )}
              <div className="flex-1 min-w-[160px]">
                <p className="text-sm font-bold text-emerald-900">✅ Créa {draft.creative.type === 'video' ? 'vidéo' : 'image'} attachée</p>
                <p className="text-xs text-emerald-700">Générée dans le Cinema Studio — utilisez son URL dans le champ créatif ci-dessous.</p>
                <a href={draft.creative.url} target="_blank" rel="noreferrer" className="text-xs text-emerald-800 underline break-all">{draft.creative.url}</a>
              </div>
              <button
                onClick={() => setCreativeChoice('studio')}
                className="text-xs font-semibold text-emerald-800 border border-emerald-300 rounded-xl px-3 py-2 hover:bg-emerald-100 transition flex-shrink-0"
              >
                Régénérer ici
              </button>
            </div>
          )}

          <CreateAdProvider>
            {/* Pré-remplissage IA → accordéon (mêmes actions que le chat Alex) */}
            <PrefillDispatcher form={draft?.form} />
            {isAIMode ? (
              <div className="relative">
                <CreateAd
                  shopifyProduct={selectedProduct}
                  initialPlatform={selectedPlatform}
                  isPremium={isPremium}
                  aiMode
                  onSwitchToManual={() => setIsAIMode(false)}
                />
                <AiChatBot defaultOpen shopifyProduct={selectedProduct} />
              </div>
            ) : (
              <CreateAd
                shopifyProduct={selectedProduct}
                initialPlatform={selectedPlatform}
                isPremium={isPremium}
                onSwitchToAI={() => setIsAIMode(true)}
              />
            )}
          </CreateAdProvider>
        </>
      )}

      {/* Navigation */}
      {step < 3 && (
        <div className="flex justify-between mt-8">
          <button
            type="button"
            onClick={() => setStep((s) => s - 1)}
            disabled={step === 1}
            className="px-5 py-2 text-sm text-[#221C51] border border-[#221C51]/30 rounded-lg disabled:opacity-30 hover:bg-[#221C51]/5 transition"
          >
            ← Retour
          </button>
          <button
            type="button"
            onClick={() => (step === 2 ? goToPublish() : setStep((s) => s + 1))}
            disabled={!canNext}
            className="px-6 py-2 text-sm text-white bg-[#221C51] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#2e265e] transition"
          >
            Continuer →
          </button>
        </div>
      )}
    </div>
  );
};

export default CreateAdFromShopify;
